import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import { isScoutWriteModeGql, type ScoutWriteModeGql } from "../hooks/scoutWriteMode";
import { useScoutWriteAccessControl } from "../hooks/useScoutWriteAccessControl";

export type ScoutWriteAccessToggleProps = {
  value: ScoutWriteModeGql;
  disabled: boolean;
  onChange: (next: ScoutWriteModeGql) => void;
  variant?: "inline" | "menu";
};

export function ScoutWriteAccessToggle({
  value,
  disabled,
  onChange,
  variant = "inline",
}: ScoutWriteAccessToggleProps) {
  const { isFeatureEnabled, controlLabel, infoTooltip, options } = useScoutWriteAccessControl({
    writeMode: value,
    onWriteModeChange: onChange,
    disabled,
  });

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <Stack spacing={1} sx={variant === "menu" ? { px: 2, py: 1.5 } : { px: 2, py: 0.5 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <FormLabel component="legend" sx={{ typography: "body2", color: "text.primary", m: 0 }}>
          {controlLabel}
        </FormLabel>
        <Tooltip title={infoTooltip}>
          <IconButton size="small" aria-label="More info about Scout write mode">
            <InfoOutlined fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </Stack>
      <FormControl disabled={disabled} fullWidth>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={value}
          disabled={disabled}
          onChange={(_event, next) => {
            if (isScoutWriteModeGql(next)) {
              onChange(next);
            }
          }}
          aria-label={controlLabel}
        >
          {options.map((option) => (
            <ToggleButton key={option.value} value={option.value} sx={{ px: 1, py: 0.75 }}>
              <Typography variant="caption" component="span">
                {option.label}
              </Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FormControl>
    </Stack>
  );
}
